class StorageService {
  constructor() {
    this.sessionKey = 'chatSession';
    this.privateKeyPrefix = 'privateKey_';
    this.publicKeyPrefix = 'publicKey_';
  }

  saveSession(sessionData) {
    try {
      console.log("🔵 [Storage] Saving session:", {
        hasPublicKey: !!sessionData.publicKey,
        hasJwtToken: !!sessionData.jwtToken,
        hasUuid: !!sessionData.uuid
      });
      
      localStorage.setItem(this.sessionKey, JSON.stringify(sessionData));
      console.log("🟢 [Storage] Session saved successfully");
      return true;
    } catch (error) {
      console.error('🔴 [Storage] Failed to save session:', error);
      return false;
    }
  }

  getSession() {
    try {
      const session = localStorage.getItem(this.sessionKey);
      if (session) {
        const parsed = JSON.parse(session);
        console.log("🔵 [Storage] Session loaded:", {
          hasPublicKey: !!parsed.publicKey,
          publicKeyPreview: parsed.publicKey?.substring(0, 50),
          hasJwtToken: !!parsed.jwtToken,
          uuid: parsed.uuid
        });
        return parsed;
      }
      console.log("🟡 [Storage] No session found");
      return null;
    } catch (error) {
      console.error('🔴 [Storage] Failed to get session:', error);
      return null;
    }
  }

  clearSession() {
    try {
      localStorage.removeItem(this.sessionKey);
      console.log("🟢 [Storage] Session cleared");
      return true;
    } catch (error) {
      console.error('🔴 [Storage] Failed to clear session:', error);
      return false;
    }
  }

  // Save private key (use sessionStorage for better security - cleared when tab closes)
  savePrivateKey(uuid, privateKey) {
    try {
      if (!uuid || !privateKey) {
        console.error('🔴 [Storage] Invalid private key data:', { hasUuid: !!uuid, hasKey: !!privateKey });
        return false;
      }
      
      sessionStorage.setItem(`${this.privateKeyPrefix}${uuid}`, privateKey);
      console.log("🟢 [Storage] Private key saved for user:", uuid);
      return true;
    } catch (error) {
      console.error('🔴 [Storage] Failed to save private key:', error);
      return false;
    }
  }

  getPrivateKey(uuid) {
    try {
      if (!uuid) {
        console.error('🔴 [Storage] No UUID provided for private key retrieval');
        return null;
      }
      
      const privateKey = sessionStorage.getItem(`${this.privateKeyPrefix}${uuid}`);
      if (privateKey) {
        console.log("🔵 [Storage] Private key found for user:", uuid);
        return privateKey;
      }
      console.log("🟡 [Storage] No private key found for user:", uuid);
      return null;
    } catch (error) {
      console.error('🔴 [Storage] Failed to get private key:', error);
      return null;
    }
  }

  clearPrivateKey(uuid) {
    try {
      sessionStorage.removeItem(`${this.privateKeyPrefix}${uuid}`);
      console.log("🟢 [Storage] Private key cleared for user:", uuid);
      return true;
    } catch (error) {
      console.error('🔴 [Storage] Failed to clear private key:', error);
      return false;
    }
  }

  clearAllPrivateKeys() {
    try {
      const keys = Object.keys(sessionStorage);
      let clearedCount = 0;
      
      keys.forEach(key => {
        if (key.startsWith(this.privateKeyPrefix)) {
          sessionStorage.removeItem(key);
          clearedCount++;
        }
      });
      
      console.log(`🟢 [Storage] Cleared ${clearedCount} private keys`);
      return true;
    } catch (error) {
      console.error('🔴 [Storage] Failed to clear private keys:', error);
      return false;
    }
  }

  // NEW: Save public key (use localStorage for persistence across sessions)
  savePublicKey(uuid, publicKey) {
    try {
      if (!uuid || !publicKey) {
        console.error('🔴 [Storage] Invalid public key data:', { hasUuid: !!uuid, hasKey: !!publicKey });
        return false;
      }
      
      localStorage.setItem(`${this.publicKeyPrefix}${uuid}`, publicKey);
      console.log("🟢 [Storage] Public key saved for user:", uuid);
      console.log("🔵 [Storage] Public key preview:", publicKey.substring(0, 50));
      return true;
    } catch (error) {
      console.error('🔴 [Storage] Failed to save public key:', error);
      return false;
    }
  }

  // NEW: Get public key
  getPublicKey(uuid) {
    try {
      if (!uuid) {
        console.error('🔴 [Storage] No UUID provided for public key retrieval');
        return null;
      }
      
      const publicKey = localStorage.getItem(`${this.publicKeyPrefix}${uuid}`);
      if (publicKey) {
        console.log("🔵 [Storage] Public key found for user:", uuid);
        console.log("🔵 [Storage] Public key preview:", publicKey.substring(0, 50));
        return publicKey;
      }
      console.log("🟡 [Storage] No public key found for user:", uuid);
      return null;
    } catch (error) {
      console.error('🔴 [Storage] Failed to get public key:', error);
      return null;
    }
  }

  // NEW: Clear public key
  clearPublicKey(uuid) {
    try {
      localStorage.removeItem(`${this.publicKeyPrefix}${uuid}`);
      console.log("🟢 [Storage] Public key cleared for user:", uuid);
      return true;
    } catch (error) {
      console.error('🔴 [Storage] Failed to clear public key:', error);
      return false;
    }
  }

  // NEW: Clear all public keys
  clearAllPublicKeys() {
    try {
      const keys = Object.keys(localStorage);
      let clearedCount = 0;
      
      keys.forEach(key => {
        if (key.startsWith(this.publicKeyPrefix)) {
          localStorage.removeItem(key);
          clearedCount++;
        }
      });
      
      console.log(`🟢 [Storage] Cleared ${clearedCount} public keys`);
      return true;
    } catch (error) {
      console.error('🔴 [Storage] Failed to clear public keys:', error);
      return false;
    }
  }

  // NEW: Save both keys at once
  saveKeyPair(uuid, privateKey, publicKey) {
    const privateSaved = this.savePrivateKey(uuid, privateKey);
    const publicSaved = this.savePublicKey(uuid, publicKey);
    
    if (privateSaved && publicSaved) {
      console.log("🟢 [Storage] Key pair saved for user:", uuid);
      return true;
    } else {
      console.error("🔴 [Storage] Failed to save key pair");
      return false;
    }
  }

  // NEW: Get both keys
  getKeyPair(uuid) {
    const privateKey = this.getPrivateKey(uuid);
    const publicKey = this.getPublicKey(uuid);
    
    return {
      privateKey,
      publicKey,
      hasBoth: !!(privateKey && publicKey)
    };
  }

  // NEW: Clear all keys (both public and private)
  clearAllKeys() {
    this.clearAllPrivateKeys();
    this.clearAllPublicKeys();
    console.log("🟢 [Storage] All keys cleared");
  }

  // NEW: Check if user has keys
  hasKeys(uuid) {
    const hasPrivate = !!this.getPrivateKey(uuid);
    const hasPublic = !!this.getPublicKey(uuid);
    
    console.log(`🔵 [Storage] Key check for ${uuid}:`, { hasPrivate, hasPublic });
    return { hasPrivate, hasPublic };
  }

  // NEW: Debug function to show all stored data
  debugStorage() {
    console.log("=== STORAGE DEBUG ===");
    
    // Session
    const session = this.getSession();
    console.log("Session:", session ? {
      hasPublicKey: !!session.publicKey,
      hasJwtToken: !!session.jwtToken,
      uuid: session.uuid
    } : "None");
    
    // Private keys
    const privateKeys = [];
    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith(this.privateKeyPrefix)) {
        privateKeys.push(key.replace(this.privateKeyPrefix, ''));
      }
    });
    console.log("Private keys for users:", privateKeys);
    
    // Public keys
    const publicKeys = [];
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(this.publicKeyPrefix)) {
        publicKeys.push(key.replace(this.publicKeyPrefix, ''));
      }
    });
    console.log("Public keys for users:", publicKeys);
    
    console.log("====================");
  }
}

export default new StorageService();